// LAP-550 — Card Portefeuille : header + DataGrid stylé + delta sur variations
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import { makeGetRequest } from 'utils/api'
import { DataGrid } from 'Components/LifeDataGrid'
import { makePostRequest } from 'utils/api'
import { makePatchRequest } from 'utils/api'
import { makeDeleteRequest } from 'utils/api'
import { useTranslation } from 'react-i18next'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CreatePortfolioForm from './CreatePortfolioForm'
import CardHeader from '../../_shared/CardHeader'
import EmptyState from '../../_shared/EmptyState'
import { sharedDataGridSx } from '../../_shared/theme'
import { textCol, numberCol, moneyCol, deltaCol } from '../../_shared/gridCols'

export default function PortfolioCard() {
  const { t, i18n } = useTranslation('layout')
  const [operations, setOperations] = useState({ results: [] })
  const [selectedRows, setSelectedRows] = useState([])
  const sstop = useRef(0)
  const scroollLoading = useRef(false)

  useEffect(() => {
    fetchOperations2()
  }, [])
  useEffect(() => {
    const gridElement = document.querySelector('#portfo-card .MuiDataGrid-virtualScroller')
    if (gridElement) gridElement.addEventListener('scroll', handleScroll)
    return () => {
      if (gridElement) gridElement.removeEventListener('scroll', handleScroll)
    }
  }, [operations])

  const handleScroll = async () => {
    const gridElement = document.querySelector('#portfo-card .MuiDataGrid-virtualScroller')
    if (!gridElement) return
    const { scrollTop, scrollHeight, clientHeight, scrollLeft } = gridElement
    if (sstop.current !== scrollLeft) {
      sstop.current = scrollLeft
      return
    }
    if (scrollTop + clientHeight >= scrollHeight) {
      if (!scroollLoading.current) {
        scroollLoading.current = true
        const url = `limit=20&offset=${operations.results.length}`
        await fetchOperations2(url)
        scroollLoading.current = false
      }
    }
  }
  const fetchOperations2 = async (pagination = '') => {
    let url = `/api/investment/portfolio-investment-operations/`
    if (pagination !== '') url += '?' + pagination
    const response = await makeGetRequest(url, {}, true)
    if (response.status === 200) {
      setOperations({
        ...response.data,
        results: [...operations.results, ...response.data.results],
      })
    }
  }
  const deleteBulkOperations = async () => {
    if (selectedRows.length === 0) {
      alert(t('investments.portfolio.selectOpAlert'))
    } else {
      const response = await makePostRequest(
        '/api/investment/portfolio-investment-delete/',
        { selected: selectedRows },
        {},
        true,
      )
      if (response.status === 200) {
        const filteredOperations = operations.results.filter(
          (v) => selectedRows.indexOf(v.id) === -1,
        )
        setOperations({
          ...operations,
          results: filteredOperations,
          count: operations.count - selectedRows.length,
        })
      }
    }
  }
  // LAP-733 (GE022) — PRU et quantité modifiables : le PRU évolue avec les
  // achats/ventes et doit pouvoir être corrigé à la main sur la ligne.
  const processRowUpdate = async (newRow, oldRow) => {
    const formData = new FormData()
    formData.append('quantity', newRow.quantity)
    formData.append('average_price', newRow.average_price)
    const response = await makePatchRequest(
      `/api/investment/portfolio-investment-operations/${newRow.id}/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
      true,
    )
    if (response.status === 200) {
      setOperations((prev) => ({
        ...prev,
        results: prev.results.map((v) => (v.id === newRow.id ? response.data : v)),
      }))
      return response.data
    }
    return oldRow
  }
  const handleProcessRowUpdateError = () => {
    // LAP-733 (GE022) — jamais d'échec muet : on prévient explicitement.
    alert(t('investments.portfolio.updateError'))
  }
  const handleDeleteRow = async (id) => {
    if (!window.confirm(t('investments.portfolio.deleteRowConfirm'))) return
    const response = await makeDeleteRequest(
      `/api/investment/portfolio-investment-operations/${id}/`,
      {},
      true,
    )
    if (response.status === 204) {
      setOperations((prev) => ({
        ...prev,
        results: prev.results.filter((v) => v.id !== id),
        count: prev.count - 1,
      }))
    }
  }
  // L'API renvoie les décimaux sous forme de chaînes ("1234.50") : sans
  // `type: 'number'` et sans conversion, le tri de la carte était alphabétique
  // (« 99 » après « 1000 »). Les fabriques de _shared/gridCols s'en chargent.
  const columns = useMemo(
    () => [
      textCol({
        field: 'type',
        headerName: t('investments.portfolio.cols.type'),
        width: 56,
        editable: false,
        renderCell: (params) => (
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              backgroundColor: 'rgba(99,160,255,0.15)',
              color: '#9ec3ff',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.75,
            }}
          >
            {params.row.type}
          </Box>
        ),
      }),
      textCol({
        field: 'name',
        headerName: t('investments.portfolio.cols.title'),
        width: 140,
        editable: false,
        renderCell: (params) => <Box sx={{ fontWeight: 600 }}>{params.row.name}</Box>,
      }),
      numberCol({
        field: 'quantity',
        headerName: t('investments.portfolio.cols.quantity'),
        width: 80,
        decimals: 2,
        editable: true,
      }),
      moneyCol({
        field: 'average_price',
        headerName: t('investments.portfolio.cols.avgPrice'),
        width: 100,
        editable: true,
      }),
      moneyCol({
        field: 'current_price',
        headerName: t('investments.portfolio.cols.currentPrice'),
        width: 100,
        editable: false,
      }),
      moneyCol({
        field: 'current_value',
        headerName: t('investments.portfolio.cols.value'),
        width: 100,
        editable: false,
      }),
      deltaCol({
        field: 'value_change',
        headerName: t('investments.portfolio.cols.valueChange'),
        width: 100,
        unit: '',
        editable: false,
      }),
      moneyCol({
        field: 'annual_income',
        headerName: t('investments.portfolio.cols.income'),
        width: 100,
        editable: false,
      }),
      deltaCol({
        field: 'profitability',
        headerName: t('investments.portfolio.cols.profitability'),
        width: 110,
        editable: false,
      }),
      {
        field: 'actions',
        headerName: '',
        width: 40,
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.7)' }}
            onClick={(event) => {
              event.stopPropagation()
              handleDeleteRow(params.row.id)
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  )
  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon={<AccountTreeOutlinedIcon fontSize="small" />}
        title={t('investments.portfolio.title')}
        subtitle={`${operations?.count || operations?.results?.length || 0} positions`}
        actions={
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ajouter">
              <IconButton sx={{ color: 'rgba(255,255,255,0.85)', padding: 0.5 }}>
                <CreatePortfolioForm setOperations={setOperations} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer la sélection">
              <IconButton
                sx={{ color: 'rgba(255,255,255,0.7)', padding: 0.5 }}
                onClick={deleteBulkOperations}
              >
                <DoDisturbOnOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      <Box mt={2} height="100%" id="portfo-card" sx={{ borderTop: '1px dashed rgba(255,255,255,0.08)', pt: 1 }}>
        <DataGrid
          sx={sharedDataGridSx}
          rows={operations.results ? operations.results : []}
          columns={columns}
          pageSize={20}
          rowCount={operations.count ? operations.count : 0}
          rowsPerPageOptions={[10, 20, 40, 80, 100]}
          pagination
          onSelectionModelChange={(itm) => setSelectedRows([...itm])}
          paginationMode="server"
          hideFooter
          experimentalFeatures={{ newEditingApi: true }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          components={{
            NoRowsOverlay: () => <EmptyState label={t('investments.portfolio.noRows')} />,
            NoResultsOverlay: () => <EmptyState label={t('investments.portfolio.noRows')} />,
          }}
          checkboxSelection
        />
      </Box>
    </Box>
  )
}
